import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Card, StatusPill, cn, formatCurrency } from '@careandkey/shared';
import { supabase } from '../lib/supabase';
import { useOrg } from '../lib/org';
import { Layout, Topbar } from '../components/Layout';
import { fmtDate } from '../lib/fmt';

type Supplier = { id: string; name: string; supplier_type: string; rating: number | null; is_active: boolean; email: string | null; phone: string | null };
type Contract = { id: string; title: string; reference: string | null; starts_on: string | null; ends_on: string | null };
type Insurance = { id: string; insurance_type: string; insurer: string | null; policy_number: string | null; expires_on: string | null; coverage_amount: number | null; currency: string | null };
type Evaluation = { id: string; score: number; criteria: string | null; notes: string | null; evaluated_on: string };
type PO = { id: string; po_number: string | null; status: string; currency: string; order_date: string; total: number };

const PO_TONE: Record<string, 'open' | 'inprogress' | 'done' | 'overdue'> = { draft: 'open', approved: 'inprogress', received: 'done', cancelled: 'overdue' };

export function Suppliers() {
  const { t, i18n } = useTranslation();
  const { id: orgId, currency } = useOrg();
  const loc = i18n.language;
  const [rows, setRows] = useState<Supplier[]>([]);
  const [sel, setSel] = useState<string | null>(null);
  const [contracts, setContracts] = useState<Contract[]>([]);
  const [insurances, setInsurances] = useState<Insurance[]>([]);
  const [evals, setEvals] = useState<Evaluation[]>([]);
  const [pos, setPos] = useState<PO[]>([]);
  const [adding, setAdding] = useState(false);
  const [name, setName] = useState('');
  const [stype, setStype] = useState('supplier');
  const money = (n: number | null, c?: string | null) => formatCurrency(n ?? 0, c || currency, loc);

  const load = useCallback(async () => {
    const { data } = await supabase.from('suppliers').select('id,name,supplier_type,rating,is_active,email,phone').is('deleted_at', null).order('name');
    setRows((data as any) ?? []);
  }, []);
  useEffect(() => { void load(); }, [load]);

  const loadDetail = useCallback(async (id: string) => {
    const [c, i, e, p] = await Promise.all([
      supabase.from('supplier_contracts').select('id,title,reference,starts_on,ends_on').eq('supplier_id', id).is('deleted_at', null).order('starts_on', { ascending: false }),
      supabase.from('supplier_insurances').select('id,insurance_type,insurer,policy_number,expires_on,coverage_amount,currency').eq('supplier_id', id).is('deleted_at', null).order('expires_on'),
      supabase.from('supplier_evaluations').select('id,score,criteria,notes,evaluated_on').eq('supplier_id', id).is('deleted_at', null).order('evaluated_on', { ascending: false }),
      supabase.from('purchase_orders').select('id,po_number,status,currency,order_date,total').eq('supplier_id', id).is('deleted_at', null).order('order_date', { ascending: false }),
    ]);
    setContracts((c.data as any) ?? []); setInsurances((i.data as any) ?? []); setEvals((e.data as any) ?? []); setPos((p.data as any) ?? []);
  }, []);
  useEffect(() => { if (sel) void loadDetail(sel); }, [sel, loadDetail]);

  async function addSupplier() {
    if (!name.trim()) return;
    await supabase.from('suppliers').insert({ organization_id: orgId, name: name.trim(), supplier_type: stype });
    setName(''); setStype('supplier'); setAdding(false); await load();
  }
  async function receivePO(id: string) {
    await supabase.rpc('receive_purchase_order', { p_po: id });
    if (sel) await loadDetail(sel);
  }
  const daysTo = (d: string | null) => (d ? Math.floor((new Date(d).getTime() - Date.now()) / 86400000) : null);

  const current = rows.find((r) => r.id === sel);

  return (
    <Layout header={<Topbar actions={<Button onClick={() => setAdding((v) => !v)}>{t('sup.addSupplier')}</Button>} />}>
      <div className="flex min-h-0 flex-1">
        <div className="w-[340px] shrink-0 overflow-y-auto border-r border-border">
          {adding && (
            <div className="space-y-2 border-b border-border bg-bg p-3">
              <input value={name} onChange={(e) => setName(e.target.value)} placeholder={t('sup.name')} className="w-full rounded-md border border-border bg-card px-2.5 py-2 text-sm" />
              <select value={stype} onChange={(e) => setStype(e.target.value)} className="w-full rounded-md border border-border bg-card px-2.5 py-2 text-sm">
                {['supplier', 'subcontractor', 'both'].map((s) => <option key={s} value={s}>{t('sup.type_' + s)}</option>)}
              </select>
              <Button onClick={addSupplier} className="w-full">{t('common.save')}</Button>
            </div>
          )}
          {rows.map((s) => (
            <button key={s.id} onClick={() => setSel(s.id)} className={cn('flex w-full items-center justify-between gap-2 border-b border-divider px-4 py-3 text-left text-sm', sel === s.id ? 'bg-bg' : '')}>
              <div className="min-w-0">
                <div className="truncate font-medium">{s.name}</div>
                <div className="text-xs text-muted">{t('sup.type_' + s.supplier_type)}{s.rating ? ` · ★ ${s.rating}` : ''}</div>
              </div>
              {!s.is_active && <StatusPill tone="overdue">{t('sup.inactive')}</StatusPill>}
            </button>
          ))}
          {rows.length === 0 && <div className="px-4 py-8 text-center text-sm text-muted">{t('common.noData')}</div>}
        </div>

        <div className="min-w-0 flex-1 overflow-y-auto p-6">
          {!current && <p className="text-sm text-muted">{t('sup.selectHint')}</p>}
          {current && (
            <div className="space-y-5">
              <div className="flex items-center gap-3">
                <h1 className="text-xl font-semibold">{current.name}</h1>
                <StatusPill tone="open">{t('sup.type_' + current.supplier_type)}</StatusPill>
                {current.rating != null && <span className="text-sm text-muted">★ {current.rating}</span>}
              </div>

              <Card className="p-0">
                <div className="border-b border-border px-4 py-2.5 text-sm font-medium">{t('sup.insurances')}</div>
                <div className="divide-y divide-divider">
                  {insurances.map((i) => {
                    const d = daysTo(i.expires_on);
                    const tone = d == null ? 'open' : d < 0 ? 'overdue' : d <= 30 ? 'onhold' : 'done';
                    return (
                      <div key={i.id} className="flex items-center justify-between px-4 py-2.5 text-sm">
                        <div><span className="font-medium">{i.insurance_type}</span><span className="ml-2 text-xs text-muted">{i.insurer} {i.policy_number}</span></div>
                        <div className="flex items-center gap-2 text-xs">
                          <span className="text-muted">{i.coverage_amount ? money(i.coverage_amount, i.currency) : ''}</span>
                          {i.expires_on && <StatusPill tone={tone as any}>{d! < 0 ? t('sup.expired') : t('sup.expires', { date: fmtDate(i.expires_on, loc) })}</StatusPill>}
                        </div>
                      </div>
                    );
                  })}
                  {insurances.length === 0 && <div className="px-4 py-4 text-xs text-muted">{t('common.noData')}</div>}
                </div>
              </Card>

              <Card className="p-0">
                <div className="border-b border-border px-4 py-2.5 text-sm font-medium">{t('sup.contracts')}</div>
                <div className="divide-y divide-divider">
                  {contracts.map((c) => (
                    <div key={c.id} className="flex items-center justify-between px-4 py-2.5 text-sm">
                      <span className="font-medium">{c.title}</span>
                      <span className="text-xs text-muted">{c.starts_on && fmtDate(c.starts_on, loc)} – {c.ends_on && fmtDate(c.ends_on, loc)}</span>
                    </div>
                  ))}
                  {contracts.length === 0 && <div className="px-4 py-4 text-xs text-muted">{t('common.noData')}</div>}
                </div>
              </Card>

              <Card className="p-0">
                <div className="border-b border-border px-4 py-2.5 text-sm font-medium">{t('sup.purchaseOrders')}</div>
                <div className="divide-y divide-divider">
                  {pos.map((p) => (
                    <div key={p.id} className="flex items-center justify-between px-4 py-2.5 text-sm">
                      <div><span className="font-mono text-xs text-muted">{p.po_number ?? '—'}</span> <span className="ml-1">{fmtDate(p.order_date, loc)}</span></div>
                      <div className="flex items-center gap-2">
                        <span className="font-mono">{money(p.total, p.currency)}</span>
                        <StatusPill tone={PO_TONE[p.status] ?? 'open'}>{t('po.status_' + p.status)}</StatusPill>
                        {p.status !== 'received' && p.status !== 'cancelled' && <Button variant="ghost" onClick={() => receivePO(p.id)}>{t('po.receive')}</Button>}
                      </div>
                    </div>
                  ))}
                  {pos.length === 0 && <div className="px-4 py-4 text-xs text-muted">{t('common.noData')}</div>}
                </div>
              </Card>

              <Card className="p-0">
                <div className="border-b border-border px-4 py-2.5 text-sm font-medium">{t('sup.evaluations')}</div>
                <div className="divide-y divide-divider">
                  {evals.map((e) => (
                    <div key={e.id} className="flex items-center justify-between px-4 py-2.5 text-sm">
                      <div><span className="font-medium">★ {e.score}/5</span><span className="ml-2 text-xs text-muted">{e.criteria}</span></div>
                      <span className="text-xs text-muted">{fmtDate(e.evaluated_on, loc)}</span>
                    </div>
                  ))}
                  {evals.length === 0 && <div className="px-4 py-4 text-xs text-muted">{t('common.noData')}</div>}
                </div>
              </Card>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
}
